import React, { PropsWithChildren } from 'react'
import clsx from 'clsx'
import { Trans, useTranslation } from 'react-i18next'
import { useEnvironment } from '@hooks/useEnvironment'
import { injectDataAttributes } from '@utils/dataAttributes'
import { SVGIcon, SVGIconProps } from '../SVGIcon'
import { ButtonLink } from '../ButtonLink'
import styles from './styles/Alert.module.scss'

type AlertProps = {
  severity?: 'success' | 'info' | 'warning' | 'error'
  header?: string
  message?: string | string[]
  className?: string
  contentClassName?: string
  smallText?: boolean
  borderless?: boolean
  iconProps?: SVGIconProps
} & PropsWithChildren

export const Alert: React.FC<AlertProps> = props => {
  const {
    severity = 'info',
    header,
    message,
    className,
    contentClassName,
    smallText,
    borderless,
    iconProps,
    children,
    ...rest
  } = props
  const textLines = Array.isArray(message) ? message : message ? [message] : []

  const svgIconProps = {
    library: 'base',
    name: severity,
    className: styles.alertIcon,
    ...iconProps,
  } as SVGIconProps

  return (
    <div
      className={clsx(
        styles.container,
        styles[severity],
        smallText && styles.smallText,
        borderless && styles.borderless,
        className
      )}
      {...rest}
    >
      <SVGIcon {...svgIconProps} />
      <div className={clsx(styles.contentContainer, contentClassName)} data-name="content-container">
        {header && (
          <div className={clsx(styles.header, smallText && styles.smallText)} data-name="header">
            {header}
          </div>
        )}
        <div className={clsx(styles.content, smallText && styles.smallText)} data-name="content">
          {children && children}
          {!children &&
            textLines.length > 0 &&
            textLines.map((line, index) => (
              <span
                key={`line_${index}`}
                className={clsx(styles.message, smallText && styles.smallText)}
                dangerouslySetInnerHTML={{ __html: line }}
              ></span>
            ))}
        </div>
      </div>
    </div>
  )
}

type GenericErrorAlertProps = {
  title?: string
}

export const GenericErrorAlert: React.FC<GenericErrorAlertProps> = props => {
  const { title } = props
  const { translationNamespace } = useEnvironment()
  const { t } = useTranslation(translationNamespace)

  return (
    <Alert
      severity={'error'}
      header={title ?? t('Common.GenericError.title')}
      className={styles.genericErrorAlert}
      {...injectDataAttributes('errorAlert.genericError')}
    >
      <Trans
        t={t}
        i18nKey={'Common.GenericError.message'}
        components={{
          phone_anchor: (
            <ButtonLink
              underline
              highlight
              onClick={() => {
                window.location.href = `tel:${t('ContactInfo.phone')}`
              }}
            />
          ),
        }}
      />
    </Alert>
  )
}
